import {
    CardBreakpointRatios,
    ContentDataDefinitionLoader,
    createCollectionAvailableEvent,
    createRegisterableComponentWithData,
    FixedRatio,
    Product,
    SizePropsKey,
} from '@news-mono/web-common'
import React from 'react'
import { MediaMode } from '../../cards/CardMedia/CardMedia'
import { TeaserMode } from '../../cards/CardText/CardTeaser'
import { KickerMode } from '../../cards/Kicker/Kicker'
import { LandscapeLayout } from '../../cards/Landscape/Landscape.layouts'
import {
    LoadMore,
    LoadMoreRouteProps,
} from '../../data-controllers/LoadMore/LoadMore'
import {
    calculateExpectedCardCountFromContentData,
    CollectionRouteProps,
} from '../../collections/collections-props.routing'
import { Juliet } from '../../collections/Juliet/Juliet'
import { TimestampType } from '../../content/CardTimestamp/CardTimestamp'
import {
    ContainerWidthRatios,
    ResponsivePictureSizes,
} from '../../content/Picture/responsive'
import { AdUnitWrapper } from '../../advertising'
import { getAdUnitId } from '../../__helpers'
import { AdNoticePosition } from '../../advertising/AdUnit/AdUnit.styled'
import { StyledInterlacedAd } from './Juliet.styled'

export interface JulietRouteProps
    extends CollectionRouteProps,
        LoadMoreRouteProps {
    hideByline?: boolean
    showLargeCard?: boolean
    timestamp?: TimestampType
    cardType: LandscapeLayout | 'LandscapeWide'
    fixedRatios?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ResponsivePictureSizes
    removeHorizontalGutters?: boolean
    removeTopListPadding?: boolean
    disableImageLazyLoad?: boolean
    stickyOffset?: number | undefined
    mediaMode?: MediaMode
    teaserMode?: TeaserMode
    kickerMode?: KickerMode
    contentIsTangential?: boolean
    containerWidthRatios?: ContainerWidthRatios
    noHorizontalDividers?: boolean
    topicLevel?: 'parent'
    hideLastDivider?: boolean
    numbered?: boolean
    hideMedia?: boolean
    condensedInfo?: boolean
    allowLiveBlogMilestoneTeaser?: boolean
    initialAdOffset?: number
    cardsBetweenAds?: number
    cardsBetweenAdsOnTablets?: number
    interlacedAdSizeDefinition?: SizePropsKey
    adNoticePosition?: AdNoticePosition
    itemHeaderAs?: React.ElementType
}

export const JulietRegistration = createRegisterableComponentWithData(
    'juliet',
    ContentDataDefinitionLoader,
    (
        {
            sectionHeader,
            itemHeaderAs,
            kickerMode,
            teaserMode,
            hideByline,
            fixedRatios,
            imageWidths,
            cardType,
            showLargeCard,
            timestamp,
            removeHorizontalGutters,
            removeTopListPadding,
            loadMoreEnabled,
            verticalSpacing,
            disableImageLazyLoad,
            mediaMode,
            contentIsTangential,
            containerWidthRatios,
            noHorizontalDividers,
            topicLevel,
            hideLastDivider,
            numbered,
            condensedInfo,
            allowLiveBlogMilestoneTeaser,
            interlacedAdSizeDefinition,
            cardsBetweenAds,
            cardsBetweenAdsOnTablets,
            initialAdOffset,
            hideMedia,
            adNoticePosition = 'none',
        }: JulietRouteProps,
        data,
        services,
    ) => {
        return (
            <LoadMore
                dataDefinition={data.dataDefinitionArgs}
                services={services}
                loadMoreEnabled={loadMoreEnabled === true}
                data={data}
                onLoadMore={(loadMoreData) => {
                    services.onEvent(
                        createCollectionAvailableEvent(
                            loadMoreData.result.publications,
                            'juliet',
                            Product.TheWest,
                            services.onEvent,
                        ),
                    )
                }}
                render={(loadMoreData) => {
                    return (
                        <Juliet
                            items={
                                loadMoreData.loaded
                                    ? {
                                          loaded: true,
                                          result: loadMoreData.result
                                              .publications,
                                      }
                                    : { loaded: false }
                            }
                            itemHeaderAs={itemHeaderAs}
                            expectedCards={calculateExpectedCardCountFromContentData(
                                data.dataDefinitionArgs,
                            )}
                            sectionHeader={sectionHeader}
                            kickerMode={kickerMode}
                            teaserMode={teaserMode}
                            hideByline={hideByline}
                            hideMedia={hideMedia}
                            fixedRatios={fixedRatios}
                            imageWidths={imageWidths}
                            cardType={cardType}
                            showLargeCard={showLargeCard}
                            timestamp={timestamp}
                            removeHorizontalGutters={removeHorizontalGutters}
                            removeTopListPadding={removeTopListPadding}
                            verticalSpacing={
                                loadMoreEnabled ? verticalSpacing : 'md'
                            }
                            disableImageLazyLoad={disableImageLazyLoad}
                            onEvent={services.onEvent}
                            mediaMode={mediaMode}
                            contentIsTangential={contentIsTangential}
                            containerWidthRatios={containerWidthRatios}
                            noHorizontalDividers={noHorizontalDividers}
                            topicLevel={topicLevel}
                            hideLastDivider={hideLastDivider}
                            numbered={numbered}
                            condensedInfo={condensedInfo}
                            allowLiveBlogMilestoneTeaser={
                                allowLiveBlogMilestoneTeaser
                            }
                            interlacedAdSizeDefinition={
                                interlacedAdSizeDefinition
                            }
                            interlacedComponents={
                                cardsBetweenAds
                                    ? {
                                          advert: {
                                              component: (index) => (
                                                  <StyledInterlacedAd>
                                                      <AdUnitWrapper
                                                          adState={
                                                              services.adState
                                                          }
                                                          onEvent={
                                                              services.onEvent
                                                          }
                                                          noticePosition={
                                                              adNoticePosition
                                                          }
                                                          adType={'inline'}
                                                          unitId={getAdUnitId(
                                                              index,
                                                              0,
                                                              cardsBetweenAds,
                                                          )}
                                                      />
                                                  </StyledInterlacedAd>
                                              ),
                                              initialAdOffset,
                                              cardsBetweenAds,
                                              cardsBetweenAdsOnTablets,
                                              repetitions: cardsBetweenAds,
                                          },
                                      }
                                    : undefined
                            }
                        />
                    )
                }}
            />
        )
    },
)
